/**
 * Custom HTML  component
 *
 * @package ext-components-pro
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { BP, defineElementaryComponent, Type } from "@adapptio/lib-blueprint";

import {
	ClassList,
	TViewComponentDefinition,
	TComponentReact,
	propGroups,
	useStyleSheetRegistry,
	StyleSheet
} from "@adapptio/lib-components";
import { termsEditor } from "../../terms";

interface ComponentCustomHtml_State {}

/**
 * Custom HTML props
 */
const ComponentCustomHtml_Props = {
	markup: BP.Prop(
		BP.String({
			...termsEditor.schemas.customHtml.markup,
			default: ""
		}),
		0,
		propGroups.common
	),

	script: BP.Prop(
		BP.String({
			...termsEditor.schemas.customHtml.script,
			default: ""
		}),
		10,
		propGroups.common
	)
};

const ComponentCustomHtml_Events = {};

const ComponentCustomHtml_Definition = defineElementaryComponent<
	typeof ComponentCustomHtml_Props,
	ComponentCustomHtml_State,
	typeof ComponentCustomHtml_Events
>({
	...termsEditor.components.customHtml.component,

	name: "customhtml",

	category: "logic",

	icon: "mdi/code-tags",

	docUrl: "...",

	order: 1000,

	props: ComponentCustomHtml_Props,

	events: ComponentCustomHtml_Events,

	hidden: true,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentCustomHtml_React: TComponentReact<typeof ComponentCustomHtml_Definition> = ({
	props,
	state,
	setState,
	componentInstance,
	reactComponentClassList
}) => {
	const { script } = props;

	const elementRef = React.useRef<HTMLDivElement>();

	const { classList, idClassName } = ClassList.getElementClassListAndIdClassName(
		"cmp-custom-html",
		componentInstance.safePath,
		{ componentClassList: reactComponentClassList }
	);

	// Markup

	const markup = React.useMemo(() => {
		return <div className="cmp-custom-html__markup" dangerouslySetInnerHTML={{ __html: props.markup }} />;
	}, [ props.markup ]);

	// Script

	React.useEffect(() => {
		if (!elementRef.current || !script) {
			return;
		}

		let scriptElement: HTMLScriptElement;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		function _windowCustomHtmlScriptValidHandler(event: any) {
			if (event.detail?.idClassName === idClassName) {
				scriptElement = document.createElement("script");

				scriptElement.appendChild(document.createTextNode(`try{${script}}catch(e){}`));

				elementRef.current.appendChild(scriptElement);
			}
		}

		window.addEventListener("adpt_component_customhtml_script_valid", _windowCustomHtmlScriptValidHandler);

		const iframeElement = document.createElement("iframe");

		iframeElement.srcdoc = `<!DOCTYPE html><meta charset="utf-8"><script>
			window.addEventListener("error", (event) => {
				if (typeof event === "object" && event !== null) {
					event.preventDefault();

					console.warn("Custom HTML script:", event.message || "UNKNOWN");
				}
			});
		</script>
		<script>
			${script};

			window.parent?.dispatchEvent(new CustomEvent("adpt_component_customhtml_script_valid", {
				detail: { idClassName: "${idClassName}" }
			}));
		</script>`;

		elementRef.current.appendChild(iframeElement);

		return () => {
			if (scriptElement?.parentElement === elementRef.current) {
				elementRef.current.removeChild(scriptElement);
			}

			if (iframeElement.parentElement === elementRef.current) {
				elementRef.current.removeChild(iframeElement);
			}

			window.removeEventListener(
				"adpt_component_customhtml_script_valid",
				_windowCustomHtmlScriptValidHandler
			);
		};
	}, [ script, idClassName ]);

	return (
		<div ref={elementRef} className={classList.toClassName()}>
			{markup}
		</div>
	);
};

export const ComponentCustomHtml: TViewComponentDefinition<typeof ComponentCustomHtml_Definition> = {
	...ComponentCustomHtml_Definition,
	reactComponent: ComponentCustomHtml_React
};
